/**
 * Issue list with collapsible find-and-fix cards.
 *
 * Used by /domains/:id (authed view) and /r/:token (public shareable report).
 * The two surfaces render identical issue cards; only the surrounding chrome
 * (auth header, share/delete buttons, etc.) differs.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  HelpCircle,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { lookupFix, inferFixCode, type Fix } from '@/lib/remediation';

export interface ScanIssue {
  severity: 'critical' | 'warn' | 'info';
  message: string;
  code?: string;
}

/**
 * score.ts v3 (docs/adr/0001-scoring-applicability.md) per-component
 * applicability verdict. Mirrors api/src/lib/scan/score.ts's ComponentScore
 * shape (a separate, loose copy — web/ can't import from the api/ workspace).
 * Only present on scans from 2026-08-05 onward (`score_breakdown`, migration
 * 055); every consumer below treats it as optional and degrades to the old
 * pass/warn/fail rendering when absent.
 */
export type ComponentState = 'scored' | 'not_applicable' | 'undetermined';
export type ComponentReason = 'no_mx' | 'null_mx' | 'esp_selectors_not_enumerable' | 'dns_lookup_failed' | 'check_not_run';
export interface ComponentScore {
  state: ComponentState;
  reason?: ComponentReason;
}
export type ScoreComponents = Partial<Record<'spf' | 'dmarc' | 'ptr' | 'dkim' | 'bimi' | 'mtaSts' | 'tlsRpt' | 'blocklist', ComponentScore>>;
export interface ScoreBreakdown {
  applicableMax?: number;
  undeterminedMax?: number;
  components?: ScoreComponents;
}

/** Which score.ts v3 component key a `push()` category maps to — `general`
 *  has none (blocklist hits are handled separately below; ptr/bimi/blocklist
 *  never leave `scored`, so they're included for completeness but never hit
 *  the not_applicable/undetermined branches in practice). */
const COMPONENT_KEY_BY_CATEGORY: Partial<Record<Fix['category'], keyof NonNullable<ScoreComponents>>> = {
  spf: 'spf', dmarc: 'dmarc', dkim: 'dkim', ptr: 'ptr', 'mta-sts': 'mtaSts', 'tls-rpt': 'tlsRpt', blocklist: 'blocklist',
};

/** The neutral "we couldn't verify this" copy for an undetermined component —
 *  see docs/adr/0001-scoring-applicability.md and docs/scoring.md. Kept in
 *  sync BY HAND with api/src/lib/scan/applicability-copy.ts's undeterminedCopy
 *  (web/ can't share code with api/ across workspaces); both are covered by
 *  dedicated tests asserting the exact required copy. */
function undeterminedCopy(category: Fix['category'], reason: ComponentReason | undefined): string {
  const label = CATEGORY_LABELS[category] ?? category;
  if (reason === 'dns_lookup_failed') {
    return `We couldn't verify ${label} this scan — the DNS lookup failed (usually a transient resolver problem). Excluded from your score rather than counted as a failure. Re-scan to check again.`;
  }
  if (category === 'dkim' && reason === 'esp_selectors_not_enumerable') {
    return "We couldn't verify DKIM from DNS. Your SPF pattern matches an ESP (e.g. Amazon SES Easy DKIM, SparkPost) that mints a random per-account DKIM selector only discoverable from a signed message — not by guessing. Excluded from your score rather than counted as a failure. Verify it: paste a message header, or enter your selector.";
  }
  return `We couldn't verify ${label}. Excluded from your score rather than counted as a failure.`;
}

/** Verify-oriented fix code for an undetermined component (matches the FIXES
 *  keys added to lib/remediation.ts). */
function unverifiedFixCode(category: Fix['category']): string {
  if (category === 'dkim') return 'dkim_unverified';
  if (category === 'mta-sts') return 'mta_sts_unverified';
  if (category === 'tls-rpt') return 'tls_rpt_unverified';
  return 'generic';
}

export interface ScanBlock {
  issues?: ScanIssue[];
  [k: string]: unknown;
}

export interface BlocklistResult extends ScanBlock {
  hits?: Array<{
    blocklistName: string;
    blocklistSlug: string;
    target: string;
    authoritative: boolean;
  }>;
  healthy?: boolean;
}

/** Per-MX DANE/TLSA presence (from api/src/lib/scan/dnssec.ts). */
export interface DaneEntry {
  mxHost: string;
  tlsaPresent: boolean;
}

/**
 * DNSSEC + DANE block. Top-level on a fresh POST /scan-domain response; absent
 * on older persisted scans and on the public/anonymous tier (the scans table
 * has no dnssec column yet) — every consumer treats it as optional.
 */
export interface DnssecResult extends ScanBlock {
  enabled?: boolean;
  dsPresent?: boolean;
  dnskeyPresent?: boolean;
  dane?: DaneEntry[];
  daneEnabled?: boolean;
  checkError?: boolean;
}

/**
 * BIMI block, including the VMC (Verified Mark Certificate) fields added in the
 * 2026 pass. The vmc* fields only populate when a VMC is asserted and fetched;
 * they're absent on older scans, so all are optional.
 */
export interface BimiResult extends ScanBlock {
  found?: boolean;
  record?: string | null;
  logoUrl?: string | null;
  vmcUrl?: string | null;
  logoReachable?: boolean | null;
  vmcReachable?: boolean | null;
  vmcParsed?: boolean | null;
  vmcIssuer?: string | null;
  vmcMark?: string | null;
  vmcValidTo?: string | null;
  vmcExpired?: boolean | null;
  vmcIssuerApproved?: boolean | null;
  vmcHasLogotype?: boolean | null;
  displayEligible?: boolean;
}

export interface ScanRow {
  id: string;
  run_at: string;
  score: number;
  spf: ScanBlock;
  dmarc: ScanBlock;
  ptr: ScanBlock;
  dkim: ScanBlock;
  mta_sts: ScanBlock;
  tls_rpt: ScanBlock;
  mx_tls: ScanBlock;
  blocklist: BlocklistResult;
  /**
   * NEW fields the scan API now returns. Optional because they're absent on
   * older persisted scans and on the anonymous tier. The POST /scan-domain
   * response carries `dnssec` (top-level) and `bimi` (with vmc* fields); the
   * persisted `domain_scans.bimi` column carries `bimi` for authed detail.
   */
  dnssec?: DnssecResult;
  bimi?: BimiResult;
  /**
   * score.ts v3 full Score object (persisted `domain_scans.score_breakdown`,
   * migration 055). NULL/absent on scans from before 2026-08-05 — every
   * consumer below degrades to the pre-v3 pass/warn/fail rendering rather
   * than crash or mislabel (see collectIssues()'s BACKWARD COMPAT tests).
   */
  score_breakdown?: ScoreBreakdown | null;
}

export interface CollectedIssue {
  category: Fix['category'];
  severity: ScanIssue['severity'];
  message: string;
  code: string;
  contextLine?: string;
  /** True for a score.ts v3 `undetermined` component — renders the neutral
   *  "Unverified" treatment (never a checkmark, never green/pass, never
   *  warn/critical) instead of the normal severity icon. */
  unverified?: boolean;
}

const CATEGORY_LABELS: Record<Fix['category'], string> = {
  spf: 'SPF',
  dmarc: 'DMARC',
  dkim: 'DKIM',
  ptr: 'PTR (reverse DNS)',
  'mta-sts': 'MTA-STS',
  'tls-rpt': 'TLS-RPT',
  blocklist: 'Blocklists',
  general: 'Other',
};

export function collectIssues(scan: ScanRow | null): CollectedIssue[] {
  if (!scan) return [];
  const out: CollectedIssue[] = [];
  // score.ts v3 — undefined on a pre-v3 scan (or the anonymous tier before
  // migration 055), in which case every `component` lookup below is
  // undefined and push() falls through to the old unconditional behavior.
  const components = scan.score_breakdown?.components;

  const push = (category: Fix['category'], block: ScanBlock | undefined) => {
    const componentKey = COMPONENT_KEY_BY_CATEGORY[category];
    const component = componentKey ? components?.[componentKey] : undefined;

    // not_applicable: EXCLUDED from the issue list entirely — remediation
    // suppressed, nothing to fix (the check doesn't apply to this domain).
    if (component?.state === 'not_applicable') return;

    // undetermined: replace whatever the raw evaluator said with ONE neutral
    // "Unverified" entry — info severity only, never warn/critical, never a
    // pass. The raw evaluator's own message (e.g. dkim.ts's unconditional
    // "No DKIM keys found...") must not survive to the screen here — that's
    // the literal contradiction this whole change fixes.
    if (component?.state === 'undetermined') {
      out.push({
        category,
        severity: 'info',
        message: undeterminedCopy(category, component.reason),
        code: unverifiedFixCode(category),
        unverified: true,
      });
      return;
    }

    if (!block?.issues) return;
    for (const i of block.issues) {
      out.push({
        category,
        severity: i.severity,
        message: i.message,
        code: i.code ?? inferFixCode({ category, message: i.message }),
      });
    }
  };

  push('spf', scan.spf);
  push('dmarc', scan.dmarc);
  push('dkim', scan.dkim);
  push('ptr', scan.ptr);
  push('mta-sts', scan.mta_sts);
  push('tls-rpt', scan.tls_rpt);

  // Each authoritative blocklist hit becomes its own actionable issue so the
  // user sees the specific delisting URL.
  if (scan.blocklist?.hits) {
    for (const hit of scan.blocklist.hits) {
      if (!hit.authoritative) continue;
      out.push({
        category: 'blocklist',
        severity: 'critical',
        message: `Listed on ${hit.blocklistName} (${hit.target})`,
        code: 'blocklist_listed',
        contextLine: `${hit.blocklistName} · ${hit.target}`,
      });
    }
  }

  const sevRank = { critical: 0, warn: 1, info: 2 };
  return out.sort((a, b) => sevRank[a.severity] - sevRank[b.severity]);
}

export function IssueList({
  issues,
  domainName,
}: {
  issues: CollectedIssue[];
  domainName: string;
}) {
  if (issues.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 p-6">
          <CheckCircle2 className="h-6 w-6 text-success" />
          <div>
            <div className="font-medium">All checks passing.</div>
            <div className="text-xs text-muted-foreground">Nothing to fix on this domain.</div>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {issues.map((issue, idx) => (
        <IssueRow key={idx} issue={issue} domainName={domainName} />
      ))}
    </div>
  );
}

export function IssueRow({
  issue,
  domainName,
  startOpen,
}: {
  issue: CollectedIssue;
  domainName: string;
  startOpen?: boolean;
}) {
  const [open, setOpen] = useState(startOpen ?? issue.severity === 'critical');
  const fix = lookupFix(issue.code);

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/30"
      >
        <div className="flex items-start gap-3">
          {issue.unverified ? (
            <HelpCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
          ) : (
            <SeverityIcon severity={issue.severity} />
          )}
          <div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] uppercase">
                {CATEGORY_LABELS[issue.category]}
              </Badge>
              {issue.unverified && (
                <Badge variant="secondary" className="text-[10px] uppercase text-muted-foreground">
                  Unverified
                </Badge>
              )}
              <span className="font-medium">{fix.title}</span>
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">{issue.message}</div>
          </div>
        </div>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {open && (
        <CardContent className="border-t bg-muted/20 px-4 py-4">
          <p className="text-sm">{fix.summary}</p>

          <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm">
            {fix.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>

          {fix.record && (
            <div className="mt-4">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                DNS record
              </div>
              <div className="rounded-md border bg-background p-3 font-mono text-xs">
                <div className="grid grid-cols-[60px_1fr] gap-x-3 gap-y-1">
                  <span className="text-muted-foreground">Host</span>
                  <code>
                    {fix.record.host === '@' ? '@ (apex)' : `${fix.record.host}.${domainName}`}
                  </code>
                  <span className="text-muted-foreground">Type</span>
                  <code>{fix.record.type}</code>
                  <span className="text-muted-foreground">Value</span>
                  <code className="break-all">{fix.record.value}</code>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    void navigator.clipboard.writeText(fix.record!.value);
                    toast.success('Copied');
                  }}
                >
                  <Copy className="h-3 w-3" />
                  Copy value
                </Button>
              </div>
              {fix.record.note && (
                <p className="mt-1 text-xs text-muted-foreground">{fix.record.note}</p>
              )}
            </div>
          )}

          {fix.links && fix.links.length > 0 && (
            <div className="mt-4">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                References
              </div>
              <ul className="space-y-1 text-sm">
                {fix.links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {link.label}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {fix.effort && (
            <p className="mt-3 text-xs text-muted-foreground">Estimated effort: {fix.effort}</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export function SeverityIcon({ severity }: { severity: string }) {
  if (severity === 'critical')
    return <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />;
  if (severity === 'warn')
    return <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" />;
  return <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />;
}
